import {
  Wallet, TrendingUp, Users, Activity, Shield, Heart,
  Brain, AlertTriangle, Briefcase, Clock, Target, Star,
} from "lucide-react";
import { SimState, derived } from "@/data/entrepreneurshipSim";

const money = (n: number) =>
  (n < 0 ? "-$" : "$") + Math.abs(Math.round(n)).toLocaleString();

function Stat({
  icon, label, value, tone = "default",
}: { icon: React.ReactNode; label: string; value: string; tone?: "good" | "warn" | "bad" | "default" }) {
  const toneClass =
    tone === "good" ? "text-emerald-400"
    : tone === "warn" ? "text-amber-400"
    : tone === "bad" ? "text-destructive"
    : "text-foreground";
  return (
    <div className="rounded-lg border border-border/60 bg-card/70 p-2">
      <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
        {icon}
        <span className="truncate">{label}</span>
      </div>
      <p className={`font-mono text-[12px] font-bold mt-0.5 ${toneClass}`}>{value}</p>
    </div>
  );
}

export default function FounderDashboard({ s, phase }: { s: SimState; phase: string }) {
  const d = derived(s);
  const cashTone = s.cash < 0 ? "bad" : s.cash < d.burnPerMonth ? "warn" : "good";
  const runwayTone = d.runway < 1 ? "bad" : d.runway < 3 ? "warn" : "good";
  const profitTone = s.lastProfit < 0 ? "bad" : s.lastProfit === 0 ? "default" : "good";
  const burnTone = s.burnout >= 70 ? "bad" : s.burnout >= 40 ? "warn" : "good";
  const riskTone = s.riskLevel >= 70 ? "bad" : s.riskLevel >= 40 ? "warn" : "default";
  const satTone = s.satisfaction >= 70 ? "good" : s.satisfaction >= 40 ? "warn" : "bad";
  const healthTone = d.health >= 70 ? "good" : d.health >= 40 ? "warn" : "bad";

  return (
    <div className="rounded-2xl border border-primary/30 bg-gradient-to-br from-card/90 to-card/60 p-3 backdrop-blur-md">
      <div className="flex items-center justify-between mb-2">
        <p className="font-heading text-[11px] font-bold text-gradient-blue tracking-wide uppercase">
          Founder Command (Simulated)
        </p>
        <span className="text-[10px] font-mono text-muted-foreground">{phase} · M{s.month}/{s.maxMonths}</span>
      </div>
      {s.name && (
        <div className="mb-2 flex items-center gap-1.5 text-[11px] text-foreground/80">
          <Briefcase className="w-3 h-3 text-primary" />
          <span className="font-heading font-bold truncate">{s.name}</span>
          {s.businessType && <span className="text-muted-foreground">· {s.businessType}</span>}
        </div>
      )}

      <div className="grid grid-cols-3 gap-1.5">
        <Stat icon={<Wallet className="w-3 h-3" />} label="Cash" value={money(s.cash)} tone={cashTone} />
        <Stat icon={<TrendingUp className="w-3 h-3" />} label="Revenue" value={money(s.totalRevenue)} />
        <Stat icon={<Activity className="w-3 h-3" />} label="Expenses" value={money(s.totalExpenses)} />
        <Stat icon={<TrendingUp className="w-3 h-3" />} label="Last profit" value={money(s.lastProfit)} tone={profitTone} />
        <Stat icon={<Clock className="w-3 h-3" />} label="Runway" value={`${d.runway.toFixed(1)} mo`} tone={runwayTone} />
        <Stat icon={<Activity className="w-3 h-3" />} label="Fixed/mo" value={money(d.burnPerMonth)} />
        <Stat icon={<Users className="w-3 h-3" />} label="Customers" value={`${s.customers}`} />
        <Stat icon={<Target className="w-3 h-3" />} label="Conv" value={`${Math.round(s.conversionRate * 100)}%`} />
        <Stat icon={<Star className="w-3 h-3" />} label="Avg price" value={money(s.avgPrice)} />
        <Stat icon={<Heart className="w-3 h-3" />} label="Satisfaction" value={`${Math.round(s.satisfaction)}`} tone={satTone} />
        <Stat icon={<Shield className="w-3 h-3" />} label="Brand" value={`${Math.round(s.brandTrust)}`} />
        <Stat icon={<Activity className="w-3 h-3" />} label="Ops" value={`${Math.round(s.opsStability)}`} />
        <Stat icon={<Users className="w-3 h-3" />} label="Team" value={`${s.employees}`} />
        <Stat icon={<Brain className="w-3 h-3" />} label="Burnout" value={`${Math.round(s.burnout)}`} tone={burnTone} />
        <Stat icon={<AlertTriangle className="w-3 h-3" />} label="Risk" value={`${Math.round(s.riskLevel)}`} tone={riskTone} />
      </div>

      <div className="mt-2 rounded-lg border border-border/60 bg-card/70 p-2">
        <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-1">
          <span>Business health</span>
          <span className="font-mono">{Math.round(d.health)}/100</span>
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
          <div
            className={`h-full transition-all ${
              healthTone === "good" ? "bg-emerald-400" : healthTone === "warn" ? "bg-amber-400" : "bg-destructive"
            }`}
            style={{ width: `${d.health}%` }}
          />
        </div>
      </div>

      <p className="text-[9px] text-muted-foreground/70 italic mt-2 text-center">
        Simulated business — for learning only. Not a real company or financial account.
      </p>
    </div>
  );
}